import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["tagName", "buttonRef", "role", "autoFocus", "className", "cursor", "disabled", "onClick", "onKeyDown", "tabIndex"];
import '../listeners/focusStylesListener';
import classNames from 'classnames';
// These should be removed once we have 100% adoption of UIApplication
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import styled from 'styled-components';
import ShareButton from '../decorators/ShareButton';
import { setupStyledComponents } from '../listeners/setupStyledComponents';
import refObject from '../utils/propTypes/refObject';
import { getTabIndex } from '../utils/TabIndex';
import { jsx as _jsx } from "react/jsx-runtime";
setupStyledComponents();

// Omits properties that aren't included in `HTMLElementTagNameMap & SVGElementTagNameMap` (this means that we don't support the omitted elements)

// Omits properties that aren't included in `JSX.IntrinsicElements` (this means that we don't support the omitted elements)

// Includes all available props except for `tagName`

// This type is used when a `tagName` is passed to the component

// This type is used when no `tagName` is passed to the component (defaults to a div)

// The "official" type of the component's props

// This is how TS picks the correct prop types to use (depends on whether a `tagName` is provided or not)

// The generic type `T` represents the HTML tag name that is used to render the component
function UIClickableCore(_ref) {
  let {
      tagName,
      // The default value here is "div", but the default value is attached to the `TagAsComponent` variable and the `UIClickablePropsWithDefaultTagName` type (this is how we need to do it when working with a generic type that comes from `tagName`)
      buttonRef,
      role = 'button',
      autoFocus,
      className,
      cursor: __cursor = 'pointer',
      disabled = false,
      onClick,
      onKeyDown,
      tabIndex
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const TagAsComponent = tagName || 'div';
  useEffect(() => {
    if (autoFocus && buttonRef && buttonRef.current) buttonRef.current.focus();
  }, [autoFocus, buttonRef]);

  // Can't find an existing TS type that maps each tag name to its corresponding element type (e.g. {div: HTMLDivElement; span: HTMLSpanElement}), so the `KeyboardEvent` type accepts `any`
  const handleKeyDown = evt => {
    const {
      key
    } = evt;
    const target = evt.target;
    if (onKeyDown) onKeyDown(evt);

    // Ignore keydown events that bubbled up
    if (buttonRef && target !== buttonRef.current) return;

    // Simulate click on Enter/Spacebar, to emulate button behavior
    if (key === 'Enter' || key === ' ') {
      if (disabled) {
        evt.preventDefault();
        evt.stopPropagation();
        return;
      }

      // @ts-expect-error Keyboard events and mouse events do not have the same object shape
      if (onClick) onClick(evt);
      if (key === ' ') {
        evt.preventDefault(); // Prevent page from scrolling (#3575)
      }
    }
  };

  // Can't find an existing TS type that maps each tag name to its corresponding element type (e.g. {div: HTMLDivElement; span: HTMLSpanElement}), so the `MouseEvent` type accepts `any`
  const handleClick = evt => {
    if (disabled) {
      evt.preventDefault();
      evt.stopPropagation();
      return;
    }
    if (onClick) onClick(evt);
  };
  return /*#__PURE__*/_jsx(TagAsComponent, Object.assign({}, rest, {
    role: role,
    "aria-disabled": disabled,
    className: classNames('private-clickable', className),
    onClick: handleClick,
    onKeyDown: handleKeyDown,
    ref: buttonRef,
    tabIndex: getTabIndex(disabled, tabIndex, null)
  }));
}
UIClickableCore.displayName = 'UIClickable';
const UIClickable = styled(UIClickableCore).withConfig({
  displayName: "UIClickable",
  componentId: "sc-1rdbd33-0"
})(["cursor:", ";"], ({
  cursor,
  disabled
}) => disabled ? 'not-allowed' : cursor);
UIClickable.displayName = 'Styled(UIClickable)';
UIClickable.propTypes = {
  autoFocus: PropTypes.bool,
  buttonRef: refObject,
  cursor: PropTypes.string,
  disabled: PropTypes.bool,
  // @ts-expect-error There isn't an easy way to grab all possible options in JS, so a generic "string" prop type here is good enough
  tagName: PropTypes.string
};
UIClickable.defaultProps = {
  cursor: 'pointer',
  role: 'button',
  tagName: 'div',
  disabled: false
};
export default ShareButton(UIClickable);